// The demo booking API on the BossDrop backend (backend/server/booking):
// open times from Eric's own calendar (eric.holmes@bossdrop.ai), and the
// booking itself. Off production, ?bookingMock=1 in the address (or
// REACT_APP_BOOKING_MOCK=1) answers from made-up times instead, so the page
// can be tried without the backend. In mock mode a name with "taken" in it
// gets "that time was just taken", and "down" gets "booking is down".
import { wallClock, zonedToUtc } from "./zones";

export const API_BASE = process.env.REACT_APP_API_BASE || "https://api.bossdrop.ai";
export const HOST_EMAIL = "eric.holmes@bossdrop.ai";

const DAY = 86400000;
// Booking goes six months out, so times load as the visitor moves toward
// them: two weeks first, then a month at a time (the API returns at most 31
// days a call). Requests overlap by an hour, so a time that straddles the
// edge of one is whole in the next.
export const FIRST_MS = 14 * DAY;
export const CHUNK_MS = 31 * DAY;
export const OVERLAP_MS = 3600000;
const MOCK_HORIZON_DAYS = 183;

const iso = (ms) => new Date(ms).toISOString();

export function mockMode() {
  if (process.env.NODE_ENV === "production") return false;
  if (process.env.REACT_APP_BOOKING_MOCK === "1") return true;
  try {
    return /[?&]bookingMock=1(&|$)/.test(window.location.search);
  } catch {
    return false;
  }
}

async function request(path, init) {
  const res = await fetch(`${API_BASE}${path}`, init);
  let body = {};
  try {
    body = (await res.json()) || {};
  } catch {}
  return { status: res.status, body };
}

/**
 * Open times in [fromMs, toMs) (at most 31 days):
 * { ok, slots: [{ start, end }], durationMin, horizonEnd, hostName } or
 * { ok:false, error }.
 */
export async function loadRange({ fromMs, toMs, signal } = {}) {
  if (mockMode()) return mockAvailability({ fromMs, toMs });
  try {
    const { body } = await request(`/api/booking/availability?from=${iso(fromMs)}&to=${iso(toMs)}`, { signal });
    if (!body.ok) return { ok: false, error: body.error || "booking_unavailable" };
    // The server may cover less than asked (its per-call limit, the horizon); go on from where it stopped.
    const covered = Date.parse(body.to);
    return {
      ok: true,
      loadedTo: Number.isFinite(covered) ? Math.min(covered, toMs) : Math.min(toMs, fromMs + FIRST_MS),
      slots: body.slots || [],
      durationMin: body.durationMin || 20,
      horizonEnd: body.horizonEnd,
      hostName: body.host?.name || "Eric Holmes",
    };
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    return { ok: false, error: "network" };
  }
}

/** The first two weeks, and how far ahead booking goes; `loadedTo` is where they end. */
export function loadAvailability({ signal } = {}) {
  const now = Date.now();
  return loadRange({ fromMs: now, toMs: now + FIRST_MS, signal });
}

/** { ok:true, start, end, meetLink, inviteSent } or { ok:false, status, error }. */
export async function createBooking(payload) {
  if (mockMode()) return mockBooking(payload);
  try {
    const { status, body } = await request("/api/booking", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (status === 200 && body.ok) return body;
    return { ok: false, status, error: body.error || (status === 429 ? "rate_limited" : "booking_unavailable") };
  } catch {
    return { ok: false, status: 0, error: "network" };
  }
}

/* ---- mock mode ---- */

// 20-minute demos, a start every 20 minutes, as the backend books them.
const MOCK_MIN = 20;

function mockAvailability({ fromMs, toMs }) {
  const tz = "America/Chicago";
  const now = Date.now();
  const horizon = now + MOCK_HORIZON_DAYS * DAY;
  const earliest = Math.max(fromMs, now + 4 * 3600000);
  const latest = Math.min(toMs, horizon);
  const slots = [];
  const today = wallClock(now, tz);
  for (let i = 0; i <= MOCK_HORIZON_DAYS; i += 1) {
    const day = new Date(Date.UTC(today.y, today.mo - 1, today.d + i));
    // Every day, weekends included, like the real booking hours.
    const dow = day.getUTCDay();
    for (let m = 7 * 60; m + MOCK_MIN <= 18 * 60; m += MOCK_MIN) {
      // Some times already taken, the same ones on every load.
      if ((i * 7 + m / MOCK_MIN) % 4 === 0 || (dow === 3 && m >= 12 * 60 && m < 14 * 60)) continue;
      const start = zonedToUtc({ y: day.getUTCFullYear(), mo: day.getUTCMonth() + 1, d: day.getUTCDate(), h: Math.floor(m / 60), mi: m % 60 }, tz);
      if (start < earliest || start + MOCK_MIN * 60000 > latest) continue;
      slots.push({ start: iso(start), end: iso(start + MOCK_MIN * 60000) });
    }
  }
  return new Promise((resolve) =>
    setTimeout(
      () => resolve({ ok: true, loadedTo: Math.min(toMs, horizon), slots, durationMin: MOCK_MIN, horizonEnd: iso(horizon), hostName: "Eric Holmes" }),
      350,
    ),
  );
}

function mockBooking(payload) {
  const name = String(payload?.name || "").toLowerCase();
  return new Promise((resolve) =>
    setTimeout(() => {
      if (name.includes("taken")) return resolve({ ok: false, status: 409, error: "slot_taken" });
      if (name.includes("down")) return resolve({ ok: false, status: 503, error: "booking_unavailable" });
      const start = Date.parse(payload.start);
      resolve({
        ok: true,
        start: iso(start),
        end: iso(start + MOCK_MIN * 60000),
        meetLink: "https://meet.google.com/abc-defg-hij",
        inviteSent: !name.includes("noinvite"),
      });
    }, 900),
  );
}
